import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * Loyalty points at the table - `FRONTEND.md` §9.806 and §9.807, BACKEND.md §10A.452, owner O153.
 *
 * The customer shows a six-digit code from their profile; the waiter types it into *Kód kupóna* and presses OK, as
 * for a coupon (*"they can use it in kód kupóna"*). The rules are the server's; this file holds the reading and the
 * wording, so the dialog can be tested without them.
 */

/** The points on a bill - `TablePointsOnTheBill`. */
export interface PointsOnTheBill {
  customerName: string
  points: number
  value: number
  removable: boolean
}

/** The six digits of what was typed - spaces and dashes dropped, as the server reads them - or null. */
export function codeDigits(typed: string): string | null {
  const digits = typed.replace(/[\s-]/g, '')
  return /^\d{6}$/.test(digits) ? digits : null
}

/** `Vernostné body: Jana Nováková, 200 bodov (-2,00 €)` - the one line under the field. */
export function onTheBillNote(held: PointsOnTheBill): string {
  return `${tt('Vernostné body')}: ${held.customerName}, ${formatCount(held.points)} ${tt('bodov')} (-${formatCents(
    parseMoneyToCents(held.value),
  )})`
}

/**
 * The part of the order's zľava that is not points - what the §9.464 edit box edits once points are on the bill.
 *
 * The box wrote the whole figure, and the server refuses a zľava under the points' value (§10A.452), so it is seeded
 * with the rest and saved as the rest plus the points.
 */
export function zlavaWithoutPointsCents(discountCents: number, pointsCents: number): number {
  return Math.max(0, discountCents - Math.max(0, pointsCents))
}

/**
 * `PATCH /orders/{id}` for an edited zľava on a bill that holds points - `editedDiscountPayload`'s shape, with the
 * points' value kept in `discount` and only the typed part recorded as the manual zľava.
 */
export function editedDiscountWithPoints(
  euros: string,
  operatorName: string | null,
  pointsCents: number,
): Record<string, unknown> {
  const manual = parseMoneyToCents(euros)
  const points = Math.max(0, pointsCents)
  if (manual <= 0) {
    return { discount: points / 100, manualDiscountType: null, manualDiscountValue: null, manualDiscountOperator: null }
  }
  return {
    discount: (manual + points) / 100,
    manualDiscountType: 'fixed',
    manualDiscountValue: manual / 100,
    manualDiscountOperator: operatorName,
  }
}
